// frontend/src/pages/admin/AdminDashboard.jsx
/**
 * Admin Control Panel Dashboard
 * Shows system metrics, feature flags, and admin controls
 * 
 * Status Strips: At-a-glance operational awareness
 * - System status
 * - Open reports
 * - Active disputes
 * - Mature payouts pending
 */

import { useEffect, useState, useContext } from "react";
import { useNavigate } from "react-router-dom";
import apiClient from "../../api/client";
import { AuthContext } from "../../App";
import { 
  ArrowLeft, Users, Activity, Heart, TrendingUp, Zap, Shield, DollarSign, 
  Flag, MessageSquare, AlertTriangle, FileText, BarChart3, Wallet, Calendar,
  CheckCircle, XCircle, Clock, ClipboardList, BookOpen, Sparkles, Crown
} from "lucide-react";
import { createInternalAxiosConfig } from "../../utils/internalApi";
import { ADMIN_ROUTES } from "../../config/adminRoutes";
import InfoIconButton from "../../components/ui/InfoIconButton";
import BuildInfo from "../../components/BuildInfo";
import AdminHelp from "../../components/admin/AdminHelp";
import DebugSnapshotButton from "../../components/admin/DebugSnapshotButton";


// Formatters
function formatMoney(cents) {
  const dollars = (cents || 0) / 100;
  return dollars.toLocaleString("en-US", { style: "currency", currency: "USD" });
}

function formatDuration(minutes) {
  if (!minutes || minutes < 1) return "< 1 min";
  if (minutes < 60) return `${Math.round(minutes)} min`;
  const hours = Math.floor(minutes / 60);
  const mins = Math.round(minutes % 60);
  if (hours < 24) return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
  const days = Math.floor(hours / 24);
  return `${days}d ${hours % 24}h`;
}

// Status indicator component
function StatusIndicator({ status, label, value, subtext, onClick }) {
  const colors = {
    green: "border-emerald-500/30 bg-emerald-500/10",
    yellow: "border-amber-500/30 bg-amber-500/10",
    red: "border-red-500/30 bg-red-500/10",
    gray: "border-slate-500/30 bg-slate-500/10",
  };
  
  const dotColors = {
    green: "bg-emerald-400",
    yellow: "bg-amber-400",
    red: "bg-red-400",
    gray: "bg-slate-400",
  };
  
  const textColors = {
    green: "text-emerald-400",
    yellow: "text-amber-400",
    red: "text-red-400",
    gray: "text-slate-400",
  };

  return (
    <button
      onClick={onClick}
      disabled={!onClick}
      className={`flex items-center gap-3 rounded-xl border ${colors[status]} p-3 sm:p-4 transition hover:opacity-80 ${onClick ? 'cursor-pointer' : 'cursor-default'} w-full sm:w-auto`}
    >
      <div className={`w-2.5 h-2.5 rounded-full ${dotColors[status]} ${status !== 'gray' ? 'animate-pulse' : ''}`} />
      <div className="text-left">
        <div className="text-xs text-slate-400">{label}</div>
        <div className={`text-sm font-semibold ${textColors[status]}`}>{value}</div>
        {subtext && <div className="text-[10px] text-slate-500">{subtext}</div>}
      </div>
    </button>
  );
}

// Metric card with context
function MetricCard({ icon: Icon, label, value, context, contextType = "neutral", iconColor = "text-blue-400", metricKey }) {
  const contextColors = {
    positive: "text-emerald-400",
    negative: "text-red-400",
    neutral: "text-slate-400",
    warning: "text-amber-400",
  };

  return (
    <div className="rounded-2xl border border-slate-800 bg-slate-900/80 p-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <span className="text-sm text-slate-400">{label}</span>
          {metricKey && <InfoIconButton metricKey={metricKey} size="xs" />}
        </div>
        <Icon className={`w-5 h-5 ${iconColor}`} />
      </div>
      <div className="text-3xl font-bold">{value}</div>
      {context && (
        <div className={`text-xs mt-1 ${contextColors[contextType]}`}>
          {context}
        </div>
      )}
    </div>
  );
}

export default function AdminDashboard() {
  const { token } = useContext(AuthContext);
  const navigate = useNavigate();
  const [summary, setSummary] = useState(null);
  const [flags, setFlags] = useState(null);
  const [guardianAlerts, setGuardianAlerts] = useState(null);
  const [payoutMetrics, setPayoutMetrics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;

    async function fetchData() {
      try {
        // Use internal headers - Admin Dashboard is an operator surface
        const config = createInternalAxiosConfig(token, 'admin');
        
        // Fetch main data in parallel
        const [meRes, summaryRes, flagsRes] = await Promise.all([
          apiClient.get(`/admin/me`, config),
          apiClient.get(`/admin/summary`, config),
          apiClient.get(`/admin/feature-flags`, config),
        ]);

        if (cancelled) return;

        console.log("[AdminDashboard] Admin verified:", meRes.data);

        setSummary(summaryRes.data);
        setFlags(flagsRes.data);
        
        // Fetch payout metrics (graceful fallback)
        try {
          const payoutRes = await apiClient.get(`/admin/payouts/unified/metrics?range=this_month`, config);
          setPayoutMetrics(payoutRes.data);
        } catch (payoutErr) {
          console.warn("[AdminDashboard] Payout metrics unavailable:", payoutErr.message);
          setPayoutMetrics(null);
        }
        
        // Fetch guardian alerts separately (graceful fallback if fails)
        try {
          const guardianRes = await apiClient.get(`/guardians/alerts/summary`, config);
          if (!cancelled) {
            setGuardianAlerts(guardianRes.data);
          }
        } catch (guardianErr) {
          console.warn("[AdminDashboard] Guardian alerts unavailable:", guardianErr.message);
          // Set neutral fallback state
          if (!cancelled) {
            setGuardianAlerts({ open_count: null, high_open_count: 0, max_severity: "unknown" });
          }
        }
      } catch (err) {
        console.error("[AdminDashboard] Failed to load admin data:", err);
        if (!cancelled) {
          setError(err.response?.data?.detail || "Failed to load admin data");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    fetchData();
    return () => {
      cancelled = true;
    };
  }, [token]);

  // Determine status colors
  const getReportsStatus = (count) => {
    if (count === 0) return "green";
    if (count <= 3) return "yellow";
    return "red";
  };

  const getDisputesStatus = (count) => {
    return count === 0 ? "green" : "red";
  };

  const getPayoutsStatus = (amount) => {
    return amount === 0 ? "green" : "yellow";
  };
  
  // Guardian alerts status (0=green, 1-3=yellow, 4+=red, HIGH overrides to red, null=gray fallback)
  const getGuardianAlertsStatus = (openCount, highCount, maxSeverity) => {
    if (openCount === null || maxSeverity === "unknown") return "gray"; // Unknown/error state
    if (highCount > 0 || maxSeverity === "critical" || maxSeverity === "error") return "red";
    if (openCount === 0) return "green";
    if (openCount <= 3) return "yellow";
    return "red";
  };

  const getActiveContext = (current, typical) => {
    if (typical === 0) return { text: "", type: "neutral" };
    const diff = current - typical;
    if (Math.abs(diff) <= 1) return { text: `Typical: ${typical}`, type: "neutral" };
    if (diff > 0) return { text: `↑ Above typical (${typical})`, type: "positive" };
    return { text: `↓ Below typical (${typical})`, type: "warning" };
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-950 via-purple-950 to-slate-950 text-slate-100 flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-purple-500 mb-4"></div>
          <div className="text-sm text-slate-400">Loading admin dashboard...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-950 via-purple-950 to-slate-950 text-slate-100 flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-400 mb-4">⚠️ {error}</div>
          <button
            onClick={() => navigate('/dashboard')}
            className="px-4 py-2 bg-slate-800 rounded-lg hover:bg-slate-700 transition"
          >
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  if (!summary) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-950 via-purple-950 to-slate-950 text-slate-100 flex items-center justify-center">
        <div className="text-sm text-red-400">Unable to load admin data.</div>
      </div>
    );
  }

  const statusData = summary.status || {};
  const activeContext = getActiveContext(summary.users_active_24h || 0, summary.typical_active_24h || 0);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-purple-950 to-slate-950 text-slate-100">
      <div className="max-w-7xl mx-auto px-4 py-6 space-y-6">
        {/* Header */}
        <header className="flex items-center justify-between border-b border-slate-800 pb-4">
          <div>
            <button
              onClick={() => navigate('/dashboard')}
              className="inline-flex items-center space-x-2 text-sm text-slate-400 hover:text-slate-200 transition mb-2"
            >
              <ArrowLeft className="w-4 h-4" />
              <span>Back to Dashboard</span>
            </button>
            <div className="flex items-center gap-2">
              <h1 className="text-3xl font-bold tracking-tight bg-gradient-to-r from-amber-400 to-orange-500 bg-clip-text text-transparent">
                Admin Control Panel
              </h1>
              <AdminHelp helpKey="admin_dashboard" size="md" />
            </div>
            <p className="text-sm text-slate-400 mt-1">
              System metrics and feature management for Unity Love
            </p>
          </div>
          <div className="flex items-center space-x-2 px-4 py-2 bg-amber-500/10 border border-amber-500/20 rounded-lg">
            <Zap className="w-4 h-4 text-amber-400" />
            <span className="text-xs font-medium text-amber-400">God Mode Active</span>
          </div>
        </header>

        {/* 1. STATUS STRIPS - At-a-glance operational awareness */}
        <section className="rounded-2xl border border-slate-800 bg-slate-900/50 p-4">
          <div className="text-xs text-slate-500 uppercase tracking-wider mb-3 font-medium">
            Do I need to act right now?
          </div>
          <div className="flex flex-wrap gap-3">
            <StatusIndicator
              status={statusData.api_online ? "green" : "red"}
              label="System"
              value={statusData.api_online ? "Online" : "Offline"}
            />
            <StatusIndicator
              status={getGuardianAlertsStatus(
                guardianAlerts?.open_count,
                guardianAlerts?.high_open_count || 0,
                guardianAlerts?.max_severity
              )}
              label="Guardian Alerts"
              value={guardianAlerts?.open_count === null ? "—" : `${guardianAlerts?.open_count || 0} open`}
              subtext={guardianAlerts?.high_open_count > 0 ? `${guardianAlerts.high_open_count} high` : undefined}
              onClick={() => navigate(ADMIN_ROUTES.GUARDIANS + '?status=open')}
            />
            <StatusIndicator
              status={getReportsStatus(statusData.open_reports || 0)}
              label="Reports"
              value={`${statusData.open_reports || 0} open`}
              onClick={() => navigate(ADMIN_ROUTES.REPORTS)}
            />
            <StatusIndicator
              status={getDisputesStatus(statusData.active_disputes || 0)}
              label="Disputes"
              value={`${statusData.active_disputes || 0} active`}
              onClick={() => navigate(ADMIN_ROUTES.DISPUTES)}
            />
            <StatusIndicator
              status={getPayoutsStatus(statusData.mature_payouts_pending || 0)}
              label="Payouts"
              value={`$${(statusData.mature_payouts_pending || 0).toFixed(2)} pending`}
              subtext="Mature & ready"
              onClick={() => navigate(ADMIN_ROUTES.LOVELINK_EARNINGS)}
            />
          </div>
        </section>

        {/* 2. SAFETY & RISK METRICS */}
        <section>
          <div className="text-xs text-slate-500 uppercase tracking-wider mb-3 font-medium">
            Safety & Risk
          </div>
          <div className="grid gap-4 md:grid-cols-2">
            <MetricCard
              icon={Activity}
              label="Active (24h)"
              value={summary.users_active_24h ?? 0}
              context={activeContext.text}
              contextType={activeContext.type}
              iconColor="text-green-400"
              metricKey="active_24h"
            />
            <MetricCard
              icon={Flag}
              label="Open Reports"
              value={statusData.open_reports ?? 0}
              context={statusData.open_reports === 0 ? "All clear" : "Needs attention"}
              contextType={statusData.open_reports === 0 ? "positive" : "warning"}
              iconColor="text-orange-400"
              metricKey="open_reports"
            />
          </div>
        </section>

        {/* 3. GROWTH METRICS */}
        <section>
          <div className="text-xs text-slate-500 uppercase tracking-wider mb-3 font-medium">
            Growth
          </div>
          <div className="grid gap-4 md:grid-cols-2">
            <MetricCard
              icon={Users}
              label="Total Users"
              value={summary.users_total ?? 0}
              context={summary.users_last_7_days > 0 ? `+${summary.users_last_7_days} in last 7 days` : "No new users this week"}
              contextType={summary.users_last_7_days > 0 ? "positive" : "neutral"}
              iconColor="text-blue-400"
              metricKey="total_users"
            />
            <MetricCard
              icon={Heart}
              label="Total Matches"
              value={summary.matches_total ?? 0}
              context={summary.matches_today > 0 ? `+${summary.matches_today} today` : "No matches yet today"}
              contextType={summary.matches_today > 0 ? "positive" : "neutral"}
              iconColor="text-rose-400"
              metricKey="total_matches"
            />
          </div>
          {summary.production_launch_at && (
            <div className="grid gap-4 md:grid-cols-2 mt-4" data-testid="launch-snapshot-tiles">
              <MetricCard
                icon={Users}
                label="Joined Since Launch"
                value={summary.users_since_launch ?? 0}
                context={`Real signups after ${new Date(summary.production_launch_at).toLocaleDateString()}`}
                contextType="positive"
                iconColor="text-emerald-400"
                metricKey="users_since_launch"
              />
              <MetricCard
                icon={Users}
                label="Pre-Launch Test Cohort"
                value={summary.pre_launch_total_users_frozen ?? 0}
                context="Frozen historical count (wiped at launch)"
                contextType="neutral"
                iconColor="text-slate-400"
                metricKey="pre_launch_frozen"
              />
            </div>
          )}
        </section>

        {/* 4. ENGAGEMENT METRICS */}
        <section>
          <div className="text-xs text-slate-500 uppercase tracking-wider mb-3 font-medium">
            Engagement
          </div>
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            <MetricCard
              icon={TrendingUp}
              label="Lounge Today"
              value={summary.lounge_sessions_today ?? 0}
              context={
                summary.lounge_sessions_today === 0 
                  ? (summary.lounge_scheduled > 0 ? `${summary.lounge_scheduled} scheduled` : "No sessions scheduled")
                  : `${summary.lounge_scheduled || 0} upcoming`
              }
              contextType={summary.lounge_sessions_today > 0 ? "positive" : "neutral"}
              iconColor="text-purple-400"
              metricKey="lounge_sessions"
            />
          </div>
        </section>

        {/* 5. FEATURE FLAGS */}
        <section className="rounded-2xl border border-slate-800 bg-slate-900/80 p-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-xl font-semibold tracking-tight">Feature Flags</h2>
              <p className="text-xs text-slate-400 mt-1">
                Toggle system features on/off
              </p>
            </div>
          </div>
          <div className="space-y-3">
            {flags &&
              Object.entries(flags).map(([key, value]) => (
                <div
                  key={key}
                  className="flex items-center justify-between rounded-xl border border-slate-800 bg-slate-950/50 px-4 py-3 hover:border-slate-700 transition"
                >
                  <div className="flex items-center space-x-3">
                    <div
                      className={`w-2 h-2 rounded-full ${
                        value ? 'bg-green-400' : 'bg-slate-600'
                      }`}
                    />
                    <div>
                      <div className="text-sm font-medium">
                        {key.split('_').map(word => 
                          word.charAt(0).toUpperCase() + word.slice(1)
                        ).join(' ')}
                      </div>
                      <div className="text-xs text-slate-500">
                        Status: {String(value)}
                      </div>
                    </div>
                  </div>
                  <button
                    type="button"
                    className={`text-xs px-4 py-2 rounded-lg border transition ${
                      value 
                        ? 'border-emerald-500/30 bg-emerald-500/10 text-emerald-400 hover:bg-emerald-500/20' 
                        : 'border-slate-700 hover:bg-slate-800'
                    }`}
                    onClick={async () => {
                      try {
                        const config = createInternalAxiosConfig(token, 'admin');
                        // Send the new value (opposite of current)
                        const res = await apiClient.post(
                          `/admin/feature-flags/${key}/toggle`,
                          { enabled: !value },
                          config
                        );
                        // Use the returned flags directly
                        if (res.data?.flags) {
                          setFlags(res.data.flags);
                        } else {
                          // Fallback: refetch flags
                          const flagsRes = await apiClient.get(
                            `/admin/feature-flags`,
                            config
                          );
                          setFlags(flagsRes.data);
                        }
                      } catch (err) {
                        console.error("[AdminDashboard] Toggle failed:", err);
                      }
                    }}
                  >
                    {value ? '✓ Enabled' : 'Disabled'}
                  </button>
                </div>
              ))}
          </div>
        </section>

        {/* 5.5 PAYOUT METRICS (Money Health) */}
        {payoutMetrics && (
          <section className="rounded-2xl border border-slate-800 bg-slate-900/80 p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <h2 className="text-xl font-semibold tracking-tight">Payout Health</h2>
                <AdminHelp helpKey="payout_metrics" />
              </div>
              <span className="text-xs text-slate-500">This month</span>
            </div>
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
              {/* Total Paid */}
              <div className="rounded-xl border border-emerald-500/20 bg-emerald-500/5 p-4">
                <div className="flex items-center gap-2 text-xs text-slate-400 mb-1">
                  <DollarSign className="w-3.5 h-3.5" />
                  Paid Out (Month)
                </div>
                <div className="text-2xl font-bold text-emerald-400">
                  {formatMoney(payoutMetrics.total_paid_cents)}
                </div>
                <div className="text-xs text-slate-500 mt-1">
                  {payoutMetrics.total_paid_count} payouts
                </div>
              </div>
              
              {/* Average Payout */}
              <div className="rounded-xl border border-blue-500/20 bg-blue-500/5 p-4">
                <div className="flex items-center gap-2 text-xs text-slate-400 mb-1">
                  <TrendingUp className="w-3.5 h-3.5" />
                  Avg Payout
                </div>
                <div className="text-2xl font-bold text-blue-400">
                  {formatMoney(payoutMetrics.avg_paid_cents)}
                </div>
                <div className="text-xs text-slate-500 mt-1">
                  Per request
                </div>
              </div>
              
              {/* Median Approval Time */}
              <div className="rounded-xl border border-violet-500/20 bg-violet-500/5 p-4">
                <div className="flex items-center gap-2 text-xs text-slate-400 mb-1">
                  <Clock className="w-3.5 h-3.5" />
                  Approval Time
                </div>
                <div className="text-2xl font-bold text-violet-400">
                  {formatDuration(payoutMetrics.median_approval_minutes)}
                </div>
                <div className="text-xs text-slate-500 mt-1">
                  Median time
                </div>
              </div>
              
              {/* Pending Requests */}
              <div 
                className={`rounded-xl border p-4 cursor-pointer transition hover:brightness-110 ${
                  payoutMetrics.pending_count > 5 
                    ? "border-amber-500/30 bg-amber-500/10" 
                    : "border-slate-700 bg-slate-800/50"
                }`}
                onClick={() => navigate(ADMIN_ROUTES.VALUE_PAYOUTS)}
              >
                <div className="flex items-center gap-2 text-xs text-slate-400 mb-1">
                  <AlertTriangle className="w-3.5 h-3.5" />
                  Pending
                </div>
                <div className={`text-2xl font-bold ${
                  payoutMetrics.pending_count > 5 ? "text-amber-400" : "text-slate-200"
                }`}>
                  {payoutMetrics.pending_count}
                </div>
                <div className="text-xs text-slate-500 mt-1">
                  {formatMoney(payoutMetrics.pending_total_cents)} awaiting
                </div>
              </div>
            </div>
          </section>
        )}

        {/* 6. ADMIN TOOLS */}
        <section>
          <div className="text-xs text-slate-500 uppercase tracking-wider mb-3 font-medium">
            Admin Tools
          </div>
          <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-4">
            <button
              onClick={() => navigate(ADMIN_ROUTES.GUARDIANS)}
              className="flex items-center gap-3 rounded-xl border border-slate-800 bg-slate-900/50 p-4 hover:bg-slate-800/50 hover:border-emerald-500/30 transition group"
            >
              <div className="p-2 rounded-lg bg-emerald-500/10 group-hover:bg-emerald-500/20 transition">
                <Shield className="w-5 h-5 text-emerald-400" />
              </div>
              <div className="text-left">
                <div className="font-medium text-slate-200">AI Guardians</div>
                <div className="text-xs text-slate-500">Safety monitoring</div>
              </div>
            </button>

            <button
              onClick={() => navigate(ADMIN_ROUTES.MODERATION)}
              className="flex items-center gap-3 rounded-xl border border-slate-800 bg-slate-900/50 p-4 hover:bg-slate-800/50 hover:border-red-500/30 transition group"
            >
              <div className="p-2 rounded-lg bg-red-500/10 group-hover:bg-red-500/20 transition">
                <Flag className="w-5 h-5 text-red-400" />
              </div>
              <div className="text-left">
                <div className="font-medium text-slate-200">Moderation</div>
                <div className="text-xs text-slate-500">User actions & bans</div>
              </div>
            </button>

            <button
              onClick={() => navigate(ADMIN_ROUTES.REPORTS)}
              className="flex items-center gap-3 rounded-xl border border-slate-800 bg-slate-900/50 p-4 hover:bg-slate-800/50 hover:border-orange-500/30 transition group"
            >
              <div className="p-2 rounded-lg bg-orange-500/10 group-hover:bg-orange-500/20 transition">
                <Flag className="w-5 h-5 text-orange-400" />
              </div>
              <div className="text-left">
                <div className="font-medium text-slate-200">Reports</div>
                <div className="text-xs text-slate-500">User reports queue</div>
              </div>
            </button>

            <button
              onClick={() => navigate(ADMIN_ROUTES.DISPUTES)}
              className="flex items-center gap-3 rounded-xl border border-slate-800 bg-slate-900/50 p-4 hover:bg-slate-800/50 hover:border-amber-500/30 transition group"
            >
              <div className="p-2 rounded-lg bg-amber-500/10 group-hover:bg-amber-500/20 transition">
                <AlertTriangle className="w-5 h-5 text-amber-400" />
              </div>
              <div className="text-left">
                <div className="font-medium text-slate-200">Disputes</div>
                <div className="text-xs text-slate-500">Chargebacks & holds</div>
              </div>
            </button>

            <button
              onClick={() => navigate(ADMIN_ROUTES.LOVELINK_EARNINGS)}
              className="flex items-center gap-3 rounded-xl border border-slate-800 bg-slate-900/50 p-4 hover:bg-slate-800/50 hover:border-purple-500/30 transition group"
            >
              <div className="p-2 rounded-lg bg-purple-500/10 group-hover:bg-purple-500/20 transition">
                <DollarSign className="w-5 h-5 text-purple-400" />
              </div>
              <div className="text-left">
                <div className="font-medium text-slate-200">LoveLink Earnings</div>
                <div className="text-xs text-slate-500">Referral payouts</div>
              </div>
            </button>

            <button
              onClick={() => navigate('/lifestyle-lounge')}
              className="flex items-center gap-3 rounded-xl border border-slate-800 bg-slate-900/50 p-4 hover:bg-slate-800/50 hover:border-blue-500/30 transition group"
            >
              <div className="p-2 rounded-lg bg-blue-500/10 group-hover:bg-blue-500/20 transition">
                <MessageSquare className="w-5 h-5 text-blue-400" />
              </div>
              <div className="text-left">
                <div className="font-medium text-slate-200">Lifestyle Lounge</div>
                <div className="text-xs text-slate-500">Community chat</div>
              </div>
            </button>

            <button
              onClick={() => navigate(ADMIN_ROUTES.AUDIT)}
              className="flex items-center gap-3 rounded-xl border border-slate-800 bg-slate-900/50 p-4 hover:bg-slate-800/50 hover:border-cyan-500/30 transition group"
            >
              <div className="p-2 rounded-lg bg-cyan-500/10 group-hover:bg-cyan-500/20 transition">
                <FileText className="w-5 h-5 text-cyan-400" />
              </div>
              <div className="text-left">
                <div className="font-medium text-slate-200">Audit Log</div>
                <div className="text-xs text-slate-500">Admin action history</div>
              </div>
            </button>

            <button
              onClick={() => navigate("/admin/comp")}
              className="flex items-center gap-3 rounded-xl border border-slate-800 bg-slate-900/50 p-4 hover:bg-slate-800/50 hover:border-amber-500/30 transition group"
              data-testid="admin-tile-comp"
            >
              <div className="p-2 rounded-lg bg-amber-500/10 group-hover:bg-amber-500/20 transition">
                <Crown className="w-5 h-5 text-amber-400" />
              </div>
              <div className="text-left">
                <div className="font-medium text-slate-200">Comp Elite</div>
                <div className="text-xs text-slate-500">Influencer & promo grants</div>
              </div>
            </button>

            <button
              onClick={() => navigate(ADMIN_ROUTES.REVENUE)}
              className="flex items-center gap-3 rounded-xl border border-slate-800 bg-slate-900/50 p-4 hover:bg-slate-800/50 hover:border-emerald-500/30 transition group"
            >
              <div className="p-2 rounded-lg bg-emerald-500/10 group-hover:bg-emerald-500/20 transition">
                <BarChart3 className="w-5 h-5 text-emerald-400" />
              </div>
              <div className="text-left">
                <div className="font-medium text-slate-200">Revenue</div>
                <div className="text-xs text-slate-500">Sales & analytics</div>
              </div>
            </button>

            <button
              onClick={() => navigate(ADMIN_ROUTES.PAYOUTS)}
              className="flex items-center gap-3 rounded-xl border border-slate-800 bg-slate-900/50 p-4 hover:bg-slate-800/50 hover:border-pink-500/30 transition group"
            >
              <div className="p-2 rounded-lg bg-pink-500/10 group-hover:bg-pink-500/20 transition">
                <Wallet className="w-5 h-5 text-pink-400" />
              </div>
              <div className="text-left">
                <div className="font-medium text-slate-200">Payouts</div>
                <div className="text-xs text-slate-500">LoveLink commissions</div>
              </div>
            </button>

            <button
              onClick={() => navigate(ADMIN_ROUTES.VALUE_PAYOUTS)}
              className="flex items-center gap-3 rounded-xl border border-slate-800 bg-slate-900/50 p-4 hover:bg-slate-800/50 hover:border-emerald-500/30 transition group"
            >
              <div className="p-2 rounded-lg bg-emerald-500/10 group-hover:bg-emerald-500/20 transition">
                <Wallet className="w-5 h-5 text-emerald-400" />
              </div>
              <div className="text-left">
                <div className="font-medium text-slate-200">Value Payouts</div>
                <div className="text-xs text-slate-500">Appreciation gifts</div>
              </div>
            </button>

            <button
              onClick={() => navigate(ADMIN_ROUTES.ANALYTICS)}
              className="flex items-center gap-3 rounded-xl border border-slate-800 bg-slate-900/50 p-4 hover:bg-slate-800/50 hover:border-indigo-500/30 transition group"
            >
              <div className="p-2 rounded-lg bg-indigo-500/10 group-hover:bg-indigo-500/20 transition">
                <Activity className="w-5 h-5 text-indigo-400" />
              </div>
              <div className="text-left">
                <div className="font-medium text-slate-200">Analytics</div>
                <div className="text-xs text-slate-500">Soft launch metrics</div>
              </div>
            </button>

            <button
              onClick={() => navigate(ADMIN_ROUTES.SPEED_DATING_OVERRIDES)}
              className="flex items-center gap-3 rounded-xl border border-slate-800 bg-slate-900/50 p-4 hover:bg-slate-800/50 hover:border-violet-500/30 transition group"
            >
              <div className="p-2 rounded-lg bg-violet-500/10 group-hover:bg-violet-500/20 transition">
                <Calendar className="w-5 h-5 text-violet-400" />
              </div>
              <div className="text-left">
                <div className="font-medium text-slate-200">Speed Dating Overrides</div>
                <div className="text-xs text-slate-500">Extra event management</div>
              </div>
            </button>

            <button
              onClick={() => navigate(ADMIN_ROUTES.INITIATIVES_OPS)}
              className="flex items-center gap-3 rounded-xl border border-slate-800 bg-slate-900/50 p-4 hover:bg-slate-800/50 hover:border-teal-500/30 transition group"
            >
              <div className="p-2 rounded-lg bg-teal-500/10 group-hover:bg-teal-500/20 transition">
                <ClipboardList className="w-5 h-5 text-teal-400" />
              </div>
              <div className="text-left">
                <div className="font-medium text-slate-200">Initiatives Ops</div>
                <div className="text-xs text-slate-500">Community program checklists</div>
              </div>
            </button>

            <button
              onClick={() => navigate(ADMIN_ROUTES.ROOM_PROMPTS)}
              className="flex items-center gap-3 rounded-xl border border-slate-800 bg-slate-900/50 p-4 hover:bg-slate-800/50 hover:border-rose-500/30 transition group"
            >
              <div className="p-2 rounded-lg bg-rose-500/10 group-hover:bg-rose-500/20 transition">
                <BookOpen className="w-5 h-5 text-rose-400" />
              </div>
              <div className="text-left">
                <div className="font-medium text-slate-200">Room Topics</div>
                <div className="text-xs text-slate-500">Manage daily prompts</div>
              </div>
            </button>

            <button
              onClick={() => navigate(ADMIN_ROUTES.SPECIAL_TOPICS)}
              className="flex items-center gap-3 rounded-xl border border-slate-800 bg-slate-900/50 p-4 hover:bg-slate-800/50 hover:border-purple-500/30 transition group"
            >
              <div className="p-2 rounded-lg bg-purple-500/10 group-hover:bg-purple-500/20 transition">
                <Sparkles className="w-5 h-5 text-purple-400" />
              </div>
              <div className="text-left">
                <div className="font-medium text-slate-200">Special Topics</div>
                <div className="text-xs text-slate-500">Holiday & event overrides</div>
              </div>
            </button>

            <button
              onClick={() => navigate(ADMIN_ROUTES.CIRCLES)}
              className="flex items-center gap-3 rounded-xl border border-slate-800 bg-slate-900/50 p-4 hover:bg-slate-800/50 hover:border-rose-500/30 transition group"
            >
              <div className="p-2 rounded-lg bg-rose-500/10 group-hover:bg-rose-500/20 transition">
                <Calendar className="w-5 h-5 text-rose-400" />
              </div>
              <div className="text-left">
                <div className="font-medium text-slate-200">Women's Circles</div>
                <div className="text-xs text-slate-500">Manage scheduled circle events</div>
              </div>
            </button>
          </div>
        </section>

        {/* 7. SYSTEM INFO (Bottom) */}
        <section className="rounded-2xl border border-slate-800 bg-slate-900/80 p-6">
          <h2 className="text-xl font-semibold tracking-tight mb-4">System Information</h2>
          <div className="grid gap-3 text-sm">
            <div className="flex justify-between py-2 border-b border-slate-800">
              <span className="text-slate-400">Environment</span>
              <span className="font-mono text-green-400">Production</span>
            </div>
            <div className="flex justify-between py-2 border-b border-slate-800">
              <span className="text-slate-400">API Status</span>
              <span className="font-mono text-green-400">● Online</span>
            </div>
            <div className="flex justify-between py-2 border-b border-slate-800">
              <span className="text-slate-400">Last Updated</span>
              <span className="font-mono text-slate-300">{new Date().toLocaleString()}</span>
            </div>
            <div className="flex justify-between py-2 border-b border-slate-800">
              <span className="text-slate-400">Build</span>
              <BuildInfo showInConsole={false} />
            </div>
            {/* Debug Snapshot - captures Rule 0 data for rapid issue diagnosis */}
            <div className="flex justify-between items-center py-3">
              <span className="text-slate-400">Debug Tools</span>
              <DebugSnapshotButton />
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}
